"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APP_BASE_OWN_ROW_ROUTE_SEGMENT = exports.AppBaseBindingRouteError = void 0;
exports.appRouteSegmentMatches = appRouteSegmentMatches;
exports.appRoutePatternMatches = appRoutePatternMatches;
exports.selectAppBaseBindingRows = selectAppBaseBindingRows;
exports.resolveAppBaseFixedRow = resolveAppBaseFixedRow;
exports.appBaseLayoutLandmark = appBaseLayoutLandmark;
class AppBaseBindingRouteError extends Error {
    constructor(status, message) {
        super(message);
        this.status = status;
    }
}
exports.AppBaseBindingRouteError = AppBaseBindingRouteError;
/** Route segment addressing the requesting principal's own row. The public
 * URL carries the literal `me`; the server resolves the row by ownership. */
exports.APP_BASE_OWN_ROW_ROUTE_SEGMENT = ':me';
/** Segment rule shared by every route dispatcher: literal segments match
 * exactly, `:me` only the literal `me`, any other `:param` one non-empty
 * segment. */
function appRouteSegmentMatches(pattern, segment) {
    // Literals, not the constants: `appFlowRuntimeScript` sends this function's
    // source to a page, so it refers to nothing outside itself.
    if (pattern === ':me')
        return segment === 'me';
    if (pattern.startsWith(':'))
        return pattern.length > 1 && Boolean(segment);
    return pattern === segment;
}
/** A declared route pattern against a requested pathname, segment by segment
 * with `appRouteSegmentMatches`; anything but two strings never matches. */
function appRoutePatternMatches(pattern, pathname) {
    if (typeof pattern !== 'string' || typeof pathname !== 'string')
        return false;
    const expected = pattern.split('/');
    const actual = pathname.split('/');
    return (expected.length === actual.length &&
        expected.every((part, i) => appRouteSegmentMatches(part, actual[i])));
}
/** Exact row selection shared by platform SSR and the local authoring runtime. */
function selectAppBaseBindingRows({ route, requestedPath, datasets, selectedRows = {}, }) {
    for (const [source, rowId] of Object.entries(selectedRows)) {
        if (!datasets[source] ||
            !datasets[source].rows.some(row => row.id === rowId))
            throw new AppBaseBindingRouteError(404, 'App row not found');
    }
    if (!route.dynamicSlugSourceKey)
        return selectedRows;
    const source = datasets[route.dynamicSlugSourceKey];
    // An unavailable read has no rows or exposed columns to resolve. Preserve
    // that state for the binding renderer instead of reporting a missing route.
    if (source && source.status === 'unavailable')
        return selectedRows;
    const pattern = route.path.split('/');
    const segments = requestedPath.split('/');
    if (pattern.includes(exports.APP_BASE_OWN_ROW_ROUTE_SEGMENT)) {
        if (pattern.length !== segments.length ||
            pattern.some((part, position) => !appRouteSegmentMatches(part, segments[position])))
            throw new AppBaseBindingRouteError(404, 'App route not found');
        // The server read this source with the viewer's ownership filter, so the
        // dataset holds exactly the viewer's own row or the route has none.
        if (!source || source.rows.length !== 1)
            throw new AppBaseBindingRouteError(404, 'App row not found');
        return { ...selectedRows, [route.dynamicSlugSourceKey]: source.rows[0].id };
    }
    if (!source || !route.dynamicSlugColumnId)
        throw new AppBaseBindingRouteError(400, 'Dynamic route requires an exposed slug column');
    if (!source.columns.some(column => column.id === route.dynamicSlugColumnId))
        throw new AppBaseBindingRouteError(400, 'Dynamic route slug column is not exposed');
    const index = pattern.indexOf(':slug');
    if (index < 0 ||
        pattern.length !== segments.length ||
        pattern.some((part, position) => position !== index && part !== segments[position]))
        throw new AppBaseBindingRouteError(404, 'App route not found');
    let slug;
    try {
        slug = decodeURIComponent(segments[index]);
    }
    catch {
        throw new AppBaseBindingRouteError(404, 'App route not found');
    }
    const rows = source.rows.filter(row => row.cells[route.dynamicSlugColumnId] === slug &&
        (!selectedRows[route.dynamicSlugSourceKey] ||
            row.id === selectedRows[route.dynamicSlugSourceKey]));
    if (rows.length !== 1)
        throw new AppBaseBindingRouteError(404, 'App row not found');
    return { ...selectedRows, [route.dynamicSlugSourceKey]: rows[0].id };
}
/**
 * Row choice of a route with `rowSelection`, shared by platform route
 * materialization, the publish check and the local authoring runtime. `key`
 * is the cell the selection addresses: the `slug` cell for `fixed-slug`
 * (matched case-insensitively), the cell of the declared column for
 * `fixed-key` (matched exactly). A row in the requested language wins over
 * language-neutral rows; more than one row in the winning group is ambiguous
 * and never resolves to an arbitrary row.
 *
 * Self-contained: the Bridge delivers this function to the frame as source
 * text, so its helpers live inside it.
 */
function resolveAppBaseFixedRow(selection, candidates, locale) {
    const keyMatches = (key) => {
        if (selection.type === 'fixed-slug')
            return (typeof key === 'string' &&
                key.trim().toLowerCase() === selection.slug.trim().toLowerCase());
        if (typeof key === 'number')
            return String(key) === selection.value;
        return typeof key === 'string' && key.trim() === selection.value;
    };
    const language = (value) => typeof value === 'string'
        ? (value.trim().toLowerCase().split(/[-_]/)[0] ?? '')
        : '';
    const matches = candidates.filter(candidate => keyMatches(candidate.key));
    const requested = language(locale);
    const localized = matches.filter(candidate => language(candidate.locale) === requested);
    const winners = localized.length
        ? localized
        : matches.filter(candidate => !language(candidate.locale));
    if (winners.length === 0)
        return { status: 'missing' };
    if (winners.length > 1)
        return { status: 'ambiguous', count: winners.length };
    return { status: 'found', candidate: winners[0] };
}
/**
 * The shared layout a link belongs to when it sits in an App's own page
 * landmark: the nearest navigation, else a header, sidebar or footer of the
 * page itself (not of its content). Such links repeat on every page like the
 * frame's navigation, so the flow draws them once. The StreamKit's flow runtime
 * in the page and the binding inspector both read links through this one
 * function; it uses nothing outside itself.
 */
function appBaseLayoutLandmark(link) {
    const nav = link.closest('nav,[role="navigation"]');
    const part = nav ??
        link.closest('header,footer,aside,[role="banner"],[role="contentinfo"],[role="complementary"]');
    if (!part ||
        (!nav && part.parentElement?.closest('main,article,section,[role="main"]')))
        return undefined;
    const kind = nav
        ? 'nav'
        : part.matches('header,footer,aside')
            ? part.localName
            : part.getAttribute('role');
    return kind + ':' + (part.getAttribute('aria-label') || 'main');
}
